import axios from 'axios';
import { getApiUrl } from '../util/getServerInfo';

/**
 * jsonRpcCall — the single canonical JSON-RPC transport for the local NVR
 * (rda-backend). It reproduces, byte-for-byte, the envelope that every
 * hand-rolled `axios()` call in `src/actions/*` and `src/util/*` builds today
 * (see `checkServerCapabilities.js` and `AuthActions.js`):
 *
 *     POST <getApiUrl()>
 *     { "jsonrpc": "2.0", "method": "<m>", "id": <n>, "params": [...] }
 *     → response.data.result = [okFlag, payloadOrSentinel]   // tuple
 *
 * WHY this uses the GLOBAL `axios` singleton (not a private instance):
 *   the session-expiry interceptor is installed on that singleton once at
 *   startup (`store.js` → `installSessionInterceptor`). Routing every call
 *   through the same `axios` means the existing `noauth → EXPIRE_SESSION`
 *   handling keeps firing for free — this is what makes collapsing the 42
 *   inline calls behind one primitive behavior-preserving rather than a
 *   silent regression.
 *
 * Conventions preserved from the existing call sites:
 *   - `jsonrpc` is the STRING "2.0" (matches checkServerCapabilities + its
 *     test; rda-backend ignores the field's exact form, both ship today).
 *   - No-arg methods (info.isAlive, info.getSerial, config.meta.getStartup
 *     with no session) OMIT the `params` key entirely rather than sending
 *     an empty array — so the wire request is identical to today's.
 *   - `url` defaults to getApiUrl() (jump-system + dev-proxy aware) but may be
 *     overridden for cross-NVR / eView login, where callers POST to an
 *     explicit `devProxyUrl(targetUrl)` instead (AuthActions.loginToServer).
 */

const DEFAULT_TIMEOUT_MS = 6000;
const JSON_RPC_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

/**
 * @param {string} method - JSON-RPC method name (e.g. 'info.isAlive')
 * @param {Array} [params] - positional params; sSess is first for authed methods
 * @param {Object} [opts]
 * @param {string} [opts.url] - endpoint override (default getApiUrl())
 * @param {number} [opts.id=1] - JSON-RPC correlation id (echoed, not multiplexed)
 * @param {number} [opts.timeout=6000] - axios timeout (ms)
 * @returns {Promise<import('axios').AxiosResponse>} the raw axios response
 */
export const jsonRpcCall = (method, params, opts = {}) => {
  const { url, id = 1, timeout = DEFAULT_TIMEOUT_MS } = opts;

  const data = { jsonrpc: '2.0', method, id };
  // Preserve the exact wire shape: include `params` only when there are some.
  if (Array.isArray(params) ? params.length > 0 : params != null) {
    data.params = params;
  }

  return axios({
    method: 'post',
    url: url || getApiUrl(),
    headers: JSON_RPC_HEADERS,
    data,
    timeout,
  });
};

/**
 * True when the response carries a successful result tuple (`result[0]` truthy).
 * @param {import('axios').AxiosResponse} response
 * @returns {boolean}
 */
export const rpcOk = (response) =>
  Array.isArray(response?.data?.result) && !!response.data.result[0];

/**
 * The payload half of the result tuple (`result[1]`), or undefined.
 * Note: this is also where the backend returns the "noauth" sentinel —
 * the global interceptor handles that case, callers usually just read this.
 * @param {import('axios').AxiosResponse} response
 * @returns {*}
 */
export const rpcPayload = (response) =>
  Array.isArray(response?.data?.result) ? response.data.result[1] : undefined;
